import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createGroup,
  groupKeys,
  listGroups,
  removeMember,
  updateGroup,
  type Group,
} from './groupsApi.ts';

const SELECTED_GROUP_KEY = 'bnb.selectedGroupId';

export function useGroups() {
  return useQuery({
    queryKey: groupKeys.all,
    queryFn: listGroups,
  });
}

export function useUpdateGroup(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (update: { name: string; allowMemberInvites: boolean }) =>
      updateGroup(groupId, update),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useRemoveMember(groupId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => removeMember(groupId, userId),
    onSuccess: async () => {
      // Refetches groups, which is also how leaving works: the group simply stops
      // coming back and useSelectedGroup falls through to another one.
      await queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

export function useCreateGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createGroup,
    onSuccess: async (group) => {
      // Persist directly rather than routing through useSelectedGroup: the new group
      // should be the one showing when the list refetches.
      persistSelectedGroup(group.id);
      await queryClient.invalidateQueries({ queryKey: groupKeys.all });
    },
  });
}

/**
 * Which group the app is currently showing.
 *
 * Persisted, because someone who lives in one household and also shares a flat list
 * should not have to re-pick every time they open the app. Falls back to the first
 * group when the stored id no longer exists — they may have left it on another device.
 */
export function useSelectedGroup(groups: Group[] | undefined) {
  const [preferredId, setPreferredId] = useState<string | null>(() => readSelectedGroup());

  // Derived, not synchronised. The stored id is a *preference*; what is actually shown
  // is whatever that preference resolves to against the groups we have. Writing the
  // fallback back into state would be a cascading render for no gain.
  const selected = useMemo(() => {
    if (!groups || groups.length === 0) {
      return null;
    }

    return groups.find((group) => group.id === preferredId) ?? groups[0] ?? null;
  }, [groups, preferredId]);

  // localStorage genuinely is an external system, so keeping it in step is what an
  // effect is for. Only the resolved value is persisted, so a stale id self-heals.
  useEffect(() => {
    if (selected) {
      persistSelectedGroup(selected.id);
    }
  }, [selected]);

  return { selected, select: setPreferredId };
}

function readSelectedGroup(): string | null {
  try {
    return localStorage.getItem(SELECTED_GROUP_KEY);
  } catch {
    return null;
  }
}

function persistSelectedGroup(groupId: string): void {
  try {
    localStorage.setItem(SELECTED_GROUP_KEY, groupId);
  } catch {
    // Non-fatal: the choice just will not survive a reload.
  }
}
